import { formatarDataHora } from '@/lib/formato';
import { ChaveAcesso } from '@/ui/components/chave-acesso';
import { Dinheiro } from '@/ui/components/dinheiro';
import { DocumentoTexto } from '@/ui/components/documento-texto';
import { CamposDetalhe } from '@/ui/patterns/campos-detalhe';
import { type DocumentoDetalhe } from '@fiscoai/contracts';

import { DirecaoComDanfe } from './direcao-danfe';
import { ORIGEM } from './rotulos';

function Participante({ titulo, p }: { titulo: string; p: DocumentoDetalhe['emitente'] | null }) {
  if (!p) return null;
  return (
    <section className="grid gap-2">
      <h3 className="text-sm font-semibold text-texto-suave">{titulo}</h3>
      <CamposDetalhe
        campos={[
          { rotulo: 'Nome', valor: p.nome, largo: true },
          { rotulo: 'CNPJ/CPF', valor: <DocumentoTexto valor={p.documento} /> },
          { rotulo: 'Inscrição estadual', valor: p.inscricaoEstadual },
          { rotulo: 'Município', valor: p.municipio ? `${p.municipio}/${p.uf ?? ''}` : p.uf },
        ]}
      />
    </section>
  );
}

export function AbaResumo({ doc }: { doc: DocumentoDetalhe }) {
  return (
    <div className="grid gap-6">
      {doc.chave && (
        <div className="grid gap-1 rounded-cartao bg-superficie-afundada px-4 py-3">
          <span className="text-xs text-texto-fraco">Chave de acesso</span>
          <ChaveAcesso chave={doc.chave} copiavel />
        </div>
      )}
      <CamposDetalhe
        campos={[
          { rotulo: 'Natureza da operação', valor: doc.naturezaOperacao, largo: true },
          { rotulo: 'Direção', valor: <DirecaoComDanfe doc={doc} />, largo: true },
          { rotulo: 'Emissão', valor: formatarDataHora(doc.emitidaEm) },
          { rotulo: 'Ambiente', valor: doc.ambiente === '1' ? 'Produção' : 'Homologação' },
          {
            rotulo: 'Protocolo',
            valor: doc.autorizacao ? (
              <span className="font-mono text-sm">{doc.autorizacao.protocolo}</span>
            ) : (
              'Sem protocolo no XML'
            ),
          },
          { rotulo: 'Assinatura', valor: doc.assinada ? 'Presente no XML' : 'Ausente' },
          { rotulo: 'Origem', valor: ORIGEM[doc.origem] },
        ]}
      />
      <Participante titulo="Emitente" p={doc.emitente} />
      <Participante titulo="Destinatário" p={doc.destinatario} />
      <section className="grid gap-2">
        <h3 className="text-sm font-semibold text-texto-suave">Totais</h3>
        <CamposDetalhe
          campos={[
            { rotulo: 'Produtos', valor: <Dinheiro valor={doc.totais.valorProdutos} /> },
            { rotulo: 'Frete', valor: <Dinheiro valor={doc.totais.valorFrete} /> },
            { rotulo: 'Desconto', valor: <Dinheiro valor={doc.totais.valorDesconto} /> },
            { rotulo: 'Outros', valor: <Dinheiro valor={doc.totais.valorOutros} /> },
            { rotulo: 'Seguro', valor: <Dinheiro valor={doc.totais.valorSeguro} /> },
            {
              rotulo: 'Total da nota',
              valor: <Dinheiro valor={doc.totais.valorTotal} className="font-semibold" />,
            },
          ]}
        />
      </section>
      {doc.informacaoComplementar && (
        <section className="grid gap-2">
          <h3 className="text-sm font-semibold text-texto-suave">Informações complementares</h3>
          <p className="whitespace-pre-line rounded-cartao border border-borda px-3 py-2 text-sm text-texto-suave">
            {doc.informacaoComplementar}
          </p>
        </section>
      )}
    </div>
  );
}
